import React, { useMemo } from "react";
import type { Source } from "../../api/useSources";
import type { Project } from "../../api/useProjects";
import { usePeriodState, type Period } from "@ui/Highcharts/hooks/usePeriod";
import { type ActiveAnalysis } from "../../api/useActiveAnalysesStream";
import { parseAbsolute, toCalendarDateTime } from "@internationalized/date";

function getPeriodCenteredOnDate(period: Period, dateRange: { pivot: number; to: number }) {
  const dateRangeMin = Math.min(dateRange.pivot, dateRange.to);
  const dateRangeMax = Math.max(dateRange.pivot, dateRange.to);

  if (dateRangeMin >= period.from && dateRangeMax <= period.to)
    return { from: period.from, to: period.to };

  const width = period.to - period.from;

  const newMin = dateRangeMin - width / 2;
  const newMax = dateRangeMax + width / 2;

  return { from: newMin, to: newMax };
}

export function useProjectPeriod({
  source,
  project,
  analyses,
  boundaries,
}: { source: Source; project: Project } & (
  | { analyses?: never; boundaries: "analyses" }
  | { analyses: ActiveAnalysis[]; boundaries: "active-analyses" }
)) {
  const extremes = useMemo(() => {
    /** @note this should never happen, we don't use period on empty sources */
    if (source.minSnapshotDate === null || source.maxSnapshotDate === null) {
      throw new Error(`source extremes of ${source.id} are not defined, cannot compute period`);
    }

    const sourceExtremes = {
      from: new Date(source.minSnapshotDate).getTime(),
      to: new Date(source.maxSnapshotDate).getTime(),
    };

    const activeAnalysisExtremes = {
      from: project.analysesAggregate?.earliestActiveAnalysis?.date
        ? new Date(project.analysesAggregate.earliestActiveAnalysis.date).getTime()
        : (analyses?.[0]?.date ?? null),
      to: project.analysesAggregate?.latestActiveAnalysis?.date
        ? new Date(project.analysesAggregate.latestActiveAnalysis.date).getTime()
        : (analyses?.[analyses.length - 1]?.date ?? null),
    };

    return {
      from:
        boundaries === "analyses"
          ? sourceExtremes.from
          : (activeAnalysisExtremes.from ?? sourceExtremes.from),
      to: new Date().getTime(),
    };
  }, [
    boundaries,
    source.id,
    source.minSnapshotDate,
    source.maxSnapshotDate,
    project.analysesAggregate,
    analyses,
  ]);

  const [period, setPeriod] = usePeriodState({
    scope: project.id,
    defaultValue: {
      from: toCalendarDateTime(parseAbsolute(new Date(extremes.to).toISOString(), project.timezone))
        .add({ months: -3 })
        .toDate(project.timezone)
        .getTime(),
      to: extremes.to,
    },
  });

  const setPeriodCenteredOnDates = React.useCallback(
    (dateRange: { pivot: number; to: number }) => {
      const newPeriod = getPeriodCenteredOnDate(period, dateRange);
      return setPeriod(newPeriod), newPeriod;
    },
    [period, setPeriod],
  );

  return React.useMemo(
    () => [period, setPeriod, extremes, setPeriodCenteredOnDates] as const,
    [period, setPeriod, extremes, setPeriodCenteredOnDates],
  );
}
